import type { Lesson } from './courses';

/**
 * Parcours 2 : Content Marketing — 7 lecons.
 */
export const CONTENT_MARKETING_LESSONS: Lesson[] = [
  {
    slug: 'content-strategy',
    title: 'Strategie de contenu : par ou commencer ?',
    duration: '4 min',
    glossaryTerms: ['content-factory', 'kpi'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "La strategie de contenu definit quoi creer, pour qui, comment et a quelle frequence. C'est le plan directeur qui aligne vos contenus avec vos objectifs business.\n\nUne bonne strategie repond a 3 questions :\n- **Qui ?** — Vos personas (profils types de clients)\n- **Quoi ?** — Les sujets et formats qui les interessent\n- **Ou ?** — Les canaux de distribution (blog, LinkedIn, email, etc.)",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Les entreprises qui publient du contenu regulierement generent 3 fois plus de leads que celles qui n'en publient pas (HubSpot). Mais publier sans strategie revient a naviguer sans carte : vous gaspillez du temps et des ressources sans garantie de resultats.\n\n70% des acheteurs B2B lisent 3 a 5 contenus avant de contacter un commercial.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Definissez vos personas** — Qui sont vos clients ideaux ? Quels sont leurs problemes ?\n2. **Identifiez les themes** — Listez 5-10 thematiques alignees avec votre expertise\n3. **Choisissez vos formats** — Articles de blog, posts LinkedIn, emails, threads\n4. **Planifiez un calendrier** — Frequence de publication realiste (1-3 contenus/semaine)\n5. **Mesurez et ajustez** — Analysez les performances et adaptez votre strategie",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "La **Content Factory** vous permet de generer rapidement differents types de contenu a partir d'un brief. Combinez-la avec le **Content Calendar** pour planifier vos publications.",
      },
    ],
    linkedModule: {
      label: 'Content Factory',
      path: '/growth/content-factory',
      icon: 'PenTool',
    },
  },
  {
    slug: 'content-types',
    title: 'Les types de contenu (articles, emails, threads, posts)',
    duration: '3 min',
    glossaryTerms: ['content-factory', 'blog-manager'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Chaque format de contenu a ses forces :\n\n- **Articles de blog** — SEO, credibilite, trafic organique long terme\n- **Posts LinkedIn** — Visibilite, engagement, personal branding\n- **Threads (Twitter/X)** — Viralite, micro-contenu, expertise rapide\n- **Emails** — Relation directe, nurturing, conversion\n- **Cold Outreach** — Prospection ciblee, personnalisation",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Diversifier ses formats augmente la portee de vos messages. Un meme sujet peut devenir un article SEO, un thread LinkedIn et un email de nurturing. Cette approche \"atomique\" maximise le ROI de chaque idee de contenu.\n\nLes entreprises multi-canal generent 3x plus d'engagement que celles qui se limitent a un seul canal.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Commencez par un article pilier** — Un contenu long et detaille sur un sujet cle\n2. **Declinez en micro-contenus** — Extrayez 3-5 posts LinkedIn, 1 thread, 2 emails\n3. **Adaptez le ton** — Blog (professionnel), LinkedIn (conversationnel), email (personnel)\n4. **Publiez sur plusieurs canaux** — Etalez les publications sur 2-3 semaines\n5. **Analysez les performances** — Quel format genere le plus d'engagement ?",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "La **Content Factory** genere des contenus dans 5 formats : post LinkedIn, email marketing, thread, cold outreach et article de blog. Selectionnez le type et renseignez votre brief.",
      },
    ],
    linkedModule: {
      label: 'Content Factory',
      path: '/growth/content-factory',
      icon: 'PenTool',
    },
  },
  {
    slug: 'editorial-calendar',
    title: 'Le calendrier editorial',
    duration: '3 min',
    glossaryTerms: ['content-calendar'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Le calendrier editorial est un outil de planification qui organise vos publications dans le temps. Il repond a 3 questions : quoi publier, quand publier et sur quel canal.\n\nUn bon calendrier doit :\n- Etre visible par toute l'equipe\n- Couvrir 2 a 4 semaines a l'avance\n- Permettre de glisser-deposer pour reorganiser",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Sans calendrier, la publication de contenu devient chaotique : periodes de suractivite suivies de silence. La regularite est la cle du content marketing. Les audiences et les algorithmes recompensent la constance.\n\nLes marques qui publient regulierement (3+ fois/semaine) ont 13x plus de chances d'obtenir un ROI positif.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Definissez votre rythme** — Combien de publications par semaine est realiste ?\n2. **Repartissez les themes** — Alternez entre education, promotion, engagement et thought leadership\n3. **Planifiez 2-4 semaines a l'avance** — Assez pour avoir de la visibilite, pas trop pour rester agile\n4. **Reservez du temps pour l'actualite** — Gardez 20% de vos creneaux pour du contenu reactif\n5. **Faites un bilan mensuel** — Quels contenus ont le mieux performe ?",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Le **Content Calendar** affiche vos publications planifiees dans une vue calendrier. Creez un contenu depuis la Content Factory, puis planifiez-le directement dans le calendrier.",
      },
    ],
    linkedModule: {
      label: 'Content Calendar',
      path: '/growth/content-calendar',
      icon: 'Calendar',
    },
  },
  {
    slug: 'content-generation-ai',
    title: "Generer du contenu avec l'IA",
    duration: '4 min',
    glossaryTerms: ['content-factory', 'rag'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "L'IA generative (GPT, Claude, etc.) permet de creer des contenus marketing de qualite en quelques minutes au lieu de quelques heures. Mais l'IA seule ne suffit pas : elle a besoin de contexte (votre produit, vos valeurs, votre ton) pour produire du contenu pertinent.\n\nC'est la ou le RAG (Retrieval-Augmented Generation) entre en jeu : l'IA puise dans votre base de connaissances pour generer du contenu contextualise.",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "Le contenu genere par IA sans contexte est generique et peu engageant. En revanche, l'IA enrichie par vos donnees produit du contenu qui ressemble a ce que vous ecririez vous-meme, mais 10x plus vite.\n\nEn moyenne, les equipes marketing qui utilisent l'IA augmentent leur production de contenu de 300% tout en maintenant la qualite.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Alimentez votre base de connaissances** — Ajoutez vos documents produit, cas clients, positionnement\n2. **Redigez des briefs precis** — Plus votre input est detaille, meilleur sera l'output\n3. **Choisissez le bon format** — Post LinkedIn, email, article de blog, thread\n4. **Editez et personnalisez** — L'IA genere un premier jet, vous apportez votre touche personnelle\n5. **Capitalisez sur les bons contenus** — Ajoutez les contenus valides a votre base de connaissances",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "La **Content Factory** utilise le RAG pour generer du contenu enrichi par votre Knowledge Base. Plus vous ajoutez de briques de connaissance dans le **Knowledge Explorer**, plus les contenus generes seront pertinents et alignes avec votre marque.",
      },
    ],
    linkedModule: {
      label: 'Knowledge Explorer',
      path: '/growth/knowledge-explorer',
      icon: 'BookOpen',
    },
  },
  {
    slug: 'linkedin-strategy',
    title: 'Strategie LinkedIn pour SaaS',
    duration: '3 min',
    glossaryTerms: ['content-factory', 'prospect-pipeline'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "LinkedIn est le canal B2B par excellence. Pour un SaaS, c'est le meilleur endroit pour :\n- Etablir son expertise (thought leadership)\n- Generer des leads qualifies\n- Construire une communaute\n- Recruter des talents\n\nLa cle : publier du contenu qui apporte de la valeur a votre audience, pas de la promotion.",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "LinkedIn genere 80% des leads B2B des reseaux sociaux. Un post bien redige peut toucher 10 000 a 100 000 personnes organiquement. C'est le canal au meilleur rapport effort/impact pour les SaaS B2B.\n\nLes fondateurs qui publient regulierement sur LinkedIn generent 2 a 5x plus de leads que ceux qui ne le font pas.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "1. **Definissez votre ligne editoriale** — 3-4 themes recurrents lies a votre expertise\n2. **Publiez 3-5 fois par semaine** — Regularite > qualite parfaite\n3. **Formats gagnants** — Histoires personnelles, listes de conseils, partage de donnees, posts controvereses\n4. **Engagez avec votre audience** — Repondez a tous les commentaires dans l'heure\n5. **Analysez vos meilleurs posts** — Identifiez les patterns qui fonctionnent et reproduisez-les",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Utilisez la **Content Factory** pour generer des posts LinkedIn optimises. Selectionnez le format \"LinkedIn Post\", renseignez votre sujet et l'IA generera un post avec un hook accrocheur et un appel a l'action.",
      },
    ],
    linkedModule: {
      label: 'Content Factory',
      path: '/growth/content-factory',
      icon: 'PenTool',
    },
  },
  {
    slug: 'email-marketing',
    title: 'Email marketing & Cold Outreach',
    duration: '4 min',
    glossaryTerms: ['cold-outreach', 'prospect-pipeline'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "L'email marketing couvre deux approches distinctes :\n\n- **Email marketing (nurturing)** — Emails envoyes a des personnes qui vous connaissent deja (newsletter, sequences d'onboarding, mises a jour produit)\n- **Cold outreach (prospection a froid)** — Emails personnalises envoyes a des prospects qui ne vous connaissent pas encore\n\nLes deux sont complementaires : le cold outreach genere des leads, le nurturing les convertit.",
      },
      {
        type: 'why',
        title: "Pourquoi c'est important ?",
        content:
          "L'email reste le canal avec le meilleur ROI marketing : 36 EUR generes pour 1 EUR investi en moyenne (Litmus). Le cold outreach bien execute affiche des taux de reponse de 5 a 15%, contre 1-2% pour les campagnes generiques.\n\nLa cle de la reussite : la personalisation. Un email personnalise a 6x plus de chances d'obtenir une reponse.",
      },
      {
        type: 'methodology',
        title: 'Comment faire ?',
        content:
          "**Pour le cold outreach :**\n1. **Ciblez precisement** — Definissez votre ICP (Ideal Customer Profile)\n2. **Recherchez avant d'ecrire** — Consultez le profil LinkedIn, l'actualite de l'entreprise\n3. **Personnalisez la premiere ligne** — Reference a un post, un projet, une actualite\n4. **Proposez de la valeur** — Pas de pitch, une ressource utile ou un insight\n5. **Relancez intelligemment** — 3 relances maximum, espacees de 3-5 jours\n\n**Pour le nurturing :**\n1. Creez une sequence de bienvenue (5-7 emails)\n2. Segmentez par comportement et interet\n3. Mesurez les taux d'ouverture et de clic",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "La **Content Factory** genere des emails marketing et des cold outreach personnalises. Le module **Sales Intelligence** vous permet de profiler vos prospects avant de les contacter, pour une personalisation maximale.",
      },
    ],
    linkedModule: {
      label: 'Sales Intelligence',
      path: '/growth/sales-intelligence',
      icon: 'Target',
    },
  },
  {
    slug: 'your-content-tools',
    title: 'Content Factory + Calendar + Blog dans FO',
    duration: '3 min',
    glossaryTerms: ['content-factory', 'content-calendar', 'blog-manager'],
    sections: [
      {
        type: 'concept',
        title: 'Le concept',
        content:
          "Feedback Orchestrator regroupe 3 outils complementaires pour votre strategie de contenu :\n\n- **Content Factory** — Generation de contenu par IA (posts, emails, articles, threads)\n- **Content Calendar** — Planification et suivi des publications\n- **Blog Manager** — Gestion des articles de blog avec SEO et auto-traduction",
      },
      {
        type: 'methodology',
        title: 'Workflow recommande',
        content:
          "1. **Ideation** — Utilisez la Content Factory pour generer des idees et premiers jets\n2. **Planification** — Placez les contenus dans le Content Calendar\n3. **Production** — Affinez les contenus generes par l'IA\n4. **Publication blog** — Pour les articles longs, utilisez le Blog Manager (SEO, slug, image a la une)\n5. **Distribution** — Publiez sur vos canaux selon le calendrier\n6. **Analyse** — Mesurez les performances dans le Marketing Dashboard",
      },
      {
        type: 'in-product',
        title: 'Dans Feedback Orchestrator',
        content:
          "Naviguez entre les trois outils depuis la barre laterale :\n- **Content Factory** pour generer\n- **Content Calendar** pour planifier\n- **Blog Manager** pour publier vos articles SEO\n\nChaque contenu cree dans la Content Factory peut etre directement planifie dans le calendrier.",
      },
    ],
    linkedModule: {
      label: 'Content Factory',
      path: '/growth/content-factory',
      icon: 'PenTool',
    },
  },
];
